import dayjs from 'dayjs';
import { IPopup } from '@/services/popups/types';
import { IMyInfo } from '@/services/user/types';

/**
 * Checks if the popup is active within the date range
 */
export const isPopupActive = (popup: IPopup): boolean => {
  const today = dayjs().format('YYYY-MM-DD');
  const startDate = dayjs(popup.startDate);
  const endDate = dayjs(popup.endDate);
  const currentDate = dayjs(today);

  return (
    currentDate.isSame(startDate) ||
    currentDate.isSame(endDate) ||
    (currentDate.isAfter(startDate) && currentDate.isBefore(endDate))
  );
};

/**
 * Checks if the user matches the popup target audience
 */
export const isUserTargeted = (popup: IPopup, userInfo: IMyInfo): boolean => {
  const { target } = popup;
  const { studentInfo } = userInfo;

  // If target is empty, show to everyone
  if (
    !target.organizations &&
    !target.campuses &&
    !target.departments &&
    !target.grades
  ) {
    return true;
  }

  // Organization check
  if (target.organizations && target.organizations.length > 0) {
    if (!target.organizations.includes(studentInfo.organization)) {
      return false;
    }
  }

  // Campus check
  if (target.campuses && target.campuses.length > 0) {
    if (!target.campuses.includes(studentInfo.campus)) {
      return false;
    }
  }

  // Department check
  if (target.departments && target.departments.length > 0) {
    if (!target.departments.includes(studentInfo.department)) {
      return false;
    }
  }

  // Grade check
  if (target.grades && target.grades.length > 0) {
    if (!target.grades.includes(studentInfo.grade)) {
      return false;
    }
  }

  return true;
};

/**
 * Checks if the user has seen the popup today
 */
export const hasUserSeenPopupToday = (
  userId: string,
  popupId: string
): boolean => {
  try {
    const today = dayjs().format('YYYY-MM-DD');
    const key = `popupSeen:${userId}:${popupId}:${today}`;
    return localStorage.getItem(key) === 'true';
  } catch (error) {
    console.error('LocalStorage popup check error:', error);
    return false;
  }
};

/**
 * Saves that the user has seen the popup today to localStorage
 */
export const markPopupAsSeenToday = (userId: string, popupId: string): void => {
  try {
    const today = dayjs().format('YYYY-MM-DD');
    const key = `popupSeen:${userId}:${popupId}:${today}`;
    localStorage.setItem(key, 'true');
  } catch (error) {
    console.error('LocalStorage popup save error:', error);
  }
};

/**
 * Cleans up old popup records (for backward compatibility)
 * @deprecated Compatibility for old system - new system works on daily basis
 */
export const hasUserSeenPopup = (userId: string, popupId: string): boolean => {
  try {
    const key = `popupSeen:${userId}:${popupId}`;
    return localStorage.getItem(key) === 'true';
  } catch (error) {
    console.error('LocalStorage popup check error:', error);
    return false;
  }
};

/**
 * Cleans up old popup records (for backward compatibility)
 * @deprecated Compatibility for old system - new system works on daily basis
 */
export const markPopupAsSeen = (userId: string, popupId: string): void => {
  try {
    const key = `popupSeen:${userId}:${popupId}`;
    localStorage.setItem(key, 'true');
  } catch (error) {
    console.error('LocalStorage popup save error:', error);
  }
};

/**
 * Cleans up old popup records (to free up space in localStorage)
 */
export const cleanupOldPopupRecords = (userId: string): void => {
  try {
    const keys = Object.keys(localStorage);
    const oldPopupKeys = keys.filter(
      (key) =>
        key.startsWith(`popupSeen:${userId}:`) &&
        !key.includes(dayjs().format('YYYY-MM-DD'))
    );

    oldPopupKeys.forEach((key) => {
      // Clean up records older than 7 days
      const keyParts = key.split(':');
      if (keyParts.length === 4) {
        const recordDate = dayjs(keyParts[3]);
        const weekAgo = dayjs().subtract(7, 'day');

        if (recordDate.isBefore(weekAgo)) {
          localStorage.removeItem(key);
        }
      } else {
        // Clean up old format records (without date info)
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Old popup records cleanup error:', error);
  }
};

/**
 * Filters popups to be displayed to the user
 */
export const getDisplayablePopups = (
  popups: IPopup[],
  userInfo: IMyInfo,
  userId: string
): IPopup[] => {
  return popups.filter((popup) => {
    // Date check
    if (!isPopupActive(popup)) {
      return false;
    }

    // Target audience check
    if (!isUserTargeted(popup, userInfo)) {
      return false;
    }

    // Today's view check
    if (hasUserSeenPopupToday(userId, popup.id)) {
      return false;
    }

    return true;
  });
};
